import { devLog, devError } from '../utils/devLogger';

declare global {
  interface Window {
    AppleID: {
      auth: {
        init: (config: any) => void;
        signIn: (config?: any) => Promise<any>;
      };
    };
  }
}

interface AppleAuthResponse {
  providerId: string;
  accessToken: string;
  userInfo: {
    email?: string;
    name?: string;
    given_name?: string;
    family_name?: string;
  };
}

export interface AppleUserInfo {
  email?: string;
  name?: string;
  sub: string; // Apple User ID
  iss: string; // Issuer
  aud: string; // Audience
  exp: number; // Expiration time
  iat: number; // Issued at
  email_verified?: boolean;
}

export interface AppleAuthData {
  email: string;
  name: string | undefined; // undefined로 변경 (SocialLoginParams와 일치)
  providerId: string;
  hasRealName: boolean; // Apple에서 실제 이름을 제공했는지 여부
}

/**
 * Base64 URL을 일반 Base64로 변환
 */
function base64UrlToBase64(base64Url: string): string {
  const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
  const pad = base64.length % 4;
  if (pad) {
    return base64 + '='.repeat(4 - pad);
  }
  return base64;
}

/**
 * JWT 토큰에서 payload를 추출하고 파싱
 */
function parseJWTPayload(token: string): any {
  try {
    const parts = token.split('.');
    if (parts.length !== 3) {
      throw new Error('Invalid JWT format');
    }

    const payload = parts[1];
    const base64 = base64UrlToBase64(payload);
    const decoded = atob(base64);
    return JSON.parse(decoded);
  } catch (error) {
    devError('JWT parsing error:', error);
    return null;
  }
}

/**
 * Apple ID Token에서 사용자 정보를 추출
 */
export function parseAppleIdToken(idToken: string): AppleUserInfo | null {
  try {
    const payload = parseJWTPayload(idToken);
    if (!payload) {
      return null;
    }

    return {
      email: payload.email || '',
      name: payload.name || '',
      sub: payload.sub || '',
      iss: payload.iss || '',
      aud: payload.aud || '',
      exp: payload.exp || 0,
      iat: payload.iat || 0,
      email_verified: payload.email_verified || false,
    };
  } catch (error) {
    devError('Apple ID Token parsing error:', error);
    return null;
  }
}

/**
 * Apple 로그인 응답에서 사용자 정보를 추출하여 API 호출용 데이터로 변환
 */
export function extractAppleUserData(
  response: any,
  userData?: any
): AppleAuthData {
  let email = '';
  let name: string | undefined = '';  // 타입을 명시적으로 지정
  let providerId = '';

  try {
    // 1. ID Token에서 정보 추출 (providerId는 sub 사용)
    if (response.authorization?.id_token) {
      const tokenInfo = parseAppleIdToken(response.authorization.id_token);
      if (tokenInfo) {
        email = tokenInfo.email || '';
        providerId = tokenInfo.sub; // Apple 사용자 고유 식별자 사용
        devLog('Apple ID Token에서 정보 추출:', {
          email: tokenInfo.email,
          sub: tokenInfo.sub,
          providerId: providerId
        });
      }
    }

    // 2. authorization code는 providerId로 사용하지 않음 (매번 변경되기 때문)
    if (response.authorization?.code) {
      devLog('Authorization Code 확인:', response.authorization.code);
    }

    // 3. 첫 로그인 시 Apple에서 제공하는 user 데이터 사용
    if (userData) {
      devLog('=== Apple에서 제공된 user 데이터 ===', userData);
      
      if (userData.email && userData.email.trim() !== '') {
        email = userData.email;
        devLog('Apple에서 이메일 획득:', email);
      }
      
      if (userData.name) {
        if (typeof userData.name === 'string') {
          name = userData.name;
          devLog('Apple에서 이름 획득 (문자열):', name);
        } else if (userData.name.firstName || userData.name.lastName) {
          name = `${userData.name.firstName || ''} ${userData.name.lastName || ''}`.trim();
          devLog('Apple에서 이름 획득 (객체):', {
            firstName: userData.name.firstName,
            lastName: userData.name.lastName,
            fullName: name
          });
        }
      } else {
        devLog('⚠️ Apple에서 이름 정보가 제공되지 않음');
      }
    } else {
      devLog('⚠️ Apple에서 user 데이터가 제공되지 않음 (재로그인이거나 권한 미동의)');
    }

    // 4. 이메일이 없으면 Apple User ID 기반으로 생성
    if (!email || email.trim() === '') {
      email = `apple_${providerId}@privaterelay.appleid.com`;
    }

    // 5. 이름 처리: Apple에서 제공하면 사용, 없으면 서버에 전달하지 않음
    let hasRealName = false;
    if (!name || name.trim() === '') {
      // Apple에서 이름을 제공하지 않는 경우 (재로그인)
      // name을 undefined로 설정하여 서버로 전달하지 않도록 함
      name = undefined;
      hasRealName = false;
      devLog('⚠️ Apple에서 이름 미제공 - 서버로 이름 필드 전달하지 않음');
    } else {
      hasRealName = !!(userData?.name && (userData.name.firstName || userData.name.lastName));
      devLog('✅ Apple에서 이름 제공됨:', name);
    }

    devLog('=== Apple 사용자 정보 추출 결과 ===');
    devLog('Email:', email);
    devLog('Name:', name);
    devLog('Provider ID:', providerId);

    return {
      email: email.trim(),
      name: name, // undefined일 수 있음 (Apple에서 이름 미제공 시)
      providerId: providerId.trim(),
      hasRealName: hasRealName, // Apple에서 실제 이름을 제공했는지 여부
    };
  } catch (error) {
    devError('Apple user data extraction error:', error);
    
    // 오류 발생 시 기본값 반환 - ID Token에서 sub을 추출할 수 없는 경우
    let fallbackProviderId = '';
    try {
      // 최후의 수단으로 ID Token에서 sub 추출 시도
      if (response.authorization?.id_token) {
        const tokenInfo = parseAppleIdToken(response.authorization.id_token);
        fallbackProviderId = tokenInfo?.sub || `apple_fallback_${Date.now()}`;
      } else {
        fallbackProviderId = `apple_fallback_${Date.now()}`;
      }
    } catch {
      fallbackProviderId = `apple_fallback_${Date.now()}`;
    }
    
    return {
      email: fallbackProviderId ? `apple_${fallbackProviderId}@privaterelay.appleid.com` : 'apple_unknown@privaterelay.appleid.com',
      name: undefined, // 오류 시에도 이름은 제공하지 않음
      providerId: fallbackProviderId,
      hasRealName: false,
    };
  }
}

// 애플 SDK 초기화
export function initAppleSDK(): boolean {
  try {
    if (typeof window === 'undefined') {
      devLog('애플 SDK: window 객체가 없음 (서버 사이드)');
      return false;
    }

    if (!window.AppleID) {
      devLog('애플 SDK가 로드되지 않음');
      return false;
    }

    // 애플 SDK 초기화
    window.AppleID.auth.init({
      clientId: process.env.REACT_APP_APPLE_CLIENT_ID || 'your-apple-client-id',
      scope: 'name email',
      redirectURI: window.location.origin,
      state: 'apple_login',
      usePopup: true
    });

    devLog('애플 SDK 초기화 성공');
    return true;
  } catch (error) {
    devError('애플 SDK 초기화 실패:', error);
    return false;
  }
}

// 애플 로그인
export async function appleLogin(): Promise<AppleAuthResponse> {
  return new Promise((resolve, reject) => {
    try {
      devLog('=== 애플 로그인 시작 ===');
      
      if (!window.AppleID) {
        devError('애플 SDK가 로드되지 않음');
        reject(new Error('애플 SDK가 로드되지 않았습니다.'));
        return;
      }

      // 애플 로그인 실행
      window.AppleID.auth.signIn()
        .then((response: any) => {
          devLog('=== 애플 로그인 SDK 응답 ===', response);
          
          if (response.authorization) {
            const authCode = response.authorization.code;
            const idToken = response.authorization.id_token;
            
            // ID 토큰에서 사용자 정보 파싱
            let userInfo: any = {};
            try {
              if (idToken) {
                const payload = JSON.parse(atob(idToken.split('.')[1]));
                userInfo = {
                  email: payload.email,
                  sub: payload.sub
                };
              }
            } catch (e) {
              devError('ID 토큰 파싱 실패:', e);
            }

            // 사용자 정보가 response.user에 있는 경우 추가
            if (response.user) {
              userInfo = {
                ...userInfo,
                ...response.user
              };
            }

            const appleResponse: AppleAuthResponse = {
              providerId: userInfo.sub || authCode,
              accessToken: idToken || authCode,
              userInfo: {
                email: userInfo.email,
                name: userInfo.name?.firstName && userInfo.name?.lastName 
                  ? `${userInfo.name.firstName} ${userInfo.name.lastName}`
                  : userInfo.name?.firstName || '애플 사용자',
                given_name: userInfo.name?.firstName,
                family_name: userInfo.name?.lastName,
              }
            };

            devLog('=== 애플 로그인 정리된 응답 ===', appleResponse);
            resolve(appleResponse);
          } else {
            devError('애플 로그인 응답에 authorization이 없음:', response);
            reject(new Error('애플 로그인에 실패했습니다.'));
          }
        })
        .catch((error: any) => {
          devError('=== 애플 로그인 SDK 에러 ===', error);
          
          // 사용자가 취소한 경우
          if (error === 'popup_closed_by_user' || error.error === 'popup_closed_by_user') {
            reject(new Error('사용자가 로그인을 취소했습니다.'));
          } else {
            reject(new Error('애플 로그인에 실패했습니다.'));
          }
        });

    } catch (error) {
      devError('=== 애플 로그인 실행 에러 ===', error);
      reject(new Error('애플 로그인 실행 중 오류가 발생했습니다.'));
    }
  });
}

// 애플 로그아웃
export function appleLogout(): void {
  try {
    // 애플은 별도의 로그아웃 SDK 메서드가 없으므로
    // 로컬 상태만 정리
    devLog('애플 로그아웃 처리됨');
  } catch (error) {
    devError('애플 로그아웃 실패:', error);
  }
}
